import React, {
    createContext,
    type ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import type { PaintProduct, RGB } from '@modules/paint-picker-new/type';
import { PaintPicker } from '../component/paint-picker/PaintPicker';
import {
    LOCAL_STORAGE_ACTIVE_KEY,
    LOCAL_STORAGE_SEEN_HINT_KEY,
    type PaintPickerAction,
    paintPickerReducer,
} from './PaintPickerReducer';
import {
    useDebounceFn,
    useLatest,
    useMemoizedFn,
    usePrevious,
    useSessionStorageState,
} from 'ahooks';
import { useTracker } from '@archipro-website/tracker';
import {
    PALETTE_COLOUR_LIMIT,
    TRACKING_PAINT_DETECTION_DEBOUNCE,
} from '../config';
import { useFetcher, useNavigate } from '@remix-run/react';
import type { PaintColour, PaintColourProduct } from '~/modules/project/api';
import { QUERY_PAINT_COLOUR_PRODUCT } from '~/routes/remix-api.paint-picker.get-paint-colour-product';
import { QUERY_PAINT_COLOURS } from '~/routes/remix-api.paint-picker.get-paint-colours';
import nearestColorLib from 'nearest-color';
import type { ErrorResponse } from '@modules/root/graphql/responses';
import {
    API_ACTIONS,
    DESIGN_BOARD_REMIX_API,
} from '~/modules/moodboards/config';
import { useOnSuccessToast } from '~/modules/moodboards/hook/use-pin-move-copy-on-sucess-toast';
import { useToaster } from '@modules/root/context/ToasterContext';
import type { SavePinsToBoardResponse } from '~/modules/moodboards/api/design-board-and-pins-api-functions/pin-functions/savePinsToDesignBoard';

interface PaintPickerContextProviderProps {
    children: ReactNode;
    containerRef?: React.RefObject<HTMLDivElement>;
    paintPickerActive?: boolean;
    bannerUrl?: string;
    projectId?: number;
    projectLink?: string;
    isDesktop?: boolean;
    isEnabled?: boolean;
}

export interface PaintPickerState {
    isEnabled: boolean;
    /** Indicates if the paint picker is currently active */
    paintPickerActive: boolean;
    /** Indicates if we are currently in the process of picking / replacing color */
    isPicking: boolean;
    /** Stores the index of the selected color, or null if no colour is selected. */
    selectedColorIndex: number | null;
    /** Determines whether to populate default values */
    populateDefaults: boolean;
    /** Holds the preview paint color, initially set to null */
    previewPaint: PaintProduct | null;
    /** Cache for storing previously selected colors */
    colourCache: Map<string, PaintProduct>;
    /** Flag to check if touch events are enabled */
    touchEnabled: boolean;
    /** Source URL of the active image, initially undefined */
    activeImageSrc?: string;
    /** Show usage tip */
    usageHintEnabled: boolean;
    /** Indicates whether a product is currently being added to the cart */
    isAddingToCart: boolean;
    /** Indicates whether the current color palette is being saved */
    isSavingPalette: boolean;
}

const PAINT_PICKER_SESSION_KEY = 'paint-picker-session';

const PaintPickerContext = createContext<
    | {
          state: PaintPickerState;
          dispatch: (action: PaintPickerAction) => void;
          paintSelection: PaintProduct[];
          matchRgbToReseneColour: (rgb: RGB) => PaintProduct | null;
          openPaintPicker: () => void;
          onSelectPreview: (
              paint: PaintProduct | null,
              fetchProductInfo?: boolean,
          ) => void;
          closePaintPicker: () => void;
          setPickingProcess: (active: boolean) => void;
          setUsageHint: (active: boolean) => void;
          addPaintToSelection: (paint?: PaintProduct) => void;
          goToProductPage: (paint: PaintProduct) => void;
          updatePaintSelection: (
              paint: PaintProduct[],
              populateDefaults?: boolean,
          ) => void;
          removePaint: (id: number) => void;
          setSelectedColour: (index: number | null) => void;
          availablePaints: PaintProduct[];
          addPaintToCart: (paint: PaintProduct) => void;
          savePaletteToDesignBoard: (boardId: number, title?: string) => void;
          containerRef: React.RefObject<HTMLDivElement> | undefined;
      }
    | undefined
>(undefined);

export const usePaintPickerContext = () => {
    const context = React.useContext(PaintPickerContext);

    if (!context) {
        throw new Error('Cannot be used outside PaintPickerContext');
    }

    return context;
};

const createPaintFormData = (paints: PaintProduct[]) => {
    const formData = new FormData();
    for (const paint of paints) {
        if (paint.link) {
            continue;
        }
        formData.append('id', paint.productId.toString());
    }

    return formData;
};

export const PaintPickerContextProvider = ({
    containerRef,
    children,
    projectId,
    projectLink,
    bannerUrl,
    isDesktop,
    isEnabled,
}: PaintPickerContextProviderProps) => {
    const designBoardTitle = 'Resene Colour Palette';

    const getSavedPickerActiveState = (): boolean => {
        if (
            !isDesktop ||
            typeof localStorage === 'undefined' ||
            !localStorage
        ) {
            return false;
        }

        return localStorage.getItem(LOCAL_STORAGE_ACTIVE_KEY) !== 'false';
    };

    const hasSeenPickerHint = () =>
        typeof localStorage !== 'undefined' && localStorage
            ? localStorage.getItem(LOCAL_STORAGE_SEEN_HINT_KEY) === 'true'
            : false;

    const [showToCartToast, setShowToCartToast] = useState(false);
    const [showSavePaletteToast, setShowSavePaletteToast] = useState(false);
    const [state, dispatch] = React.useReducer(paintPickerReducer, {
        isEnabled: !!isEnabled,
        paintPickerActive: getSavedPickerActiveState(),
        isPicking: false,
        selectedColorIndex: null,
        populateDefaults: true,
        previewPaint: null,
        colourCache: new Map(),
        touchEnabled: false,
        activeImageSrc: undefined,
        usageHintEnabled: false,
        isAddingToCart: false,
        isSavingPalette: false,
    });

    // activate usage hint
    useEffect(() => {
        dispatch({ type: 'TOGGLE_USAGE_HINT', payload: !hasSeenPickerHint() });
    }, []);

    const [paintSelection, setPaintSelection] = useSessionStorageState<
        PaintProduct[]
    >(`${PAINT_PICKER_SESSION_KEY}_${projectId}`, {
        defaultValue: [],
    });

    const [availablePaints, setAvailablePaints] = useState<PaintProduct[]>([]);

    const tracker = useTracker();
    const navigate = useNavigate();

    const fetcher = useFetcher<PaintColourProduct[]>();
    const colourFetcher = useFetcher<PaintColour[]>();
    const cartFetcher = useFetcher<PaintColour[]>();
    const savePaletteFetcher = useFetcher<
        SavePinsToBoardResponse | ErrorResponse
    >();

    const { toast } = useToaster();
    const { onSuccessToast } = useOnSuccessToast();
    const showSuccessToast = useMemoizedFn(onSuccessToast);

    const locationFetcher = useLatest(colourFetcher);

    useEffect(() => {
        if (!isEnabled || !projectLink) {
            setAvailablePaints((availablePaints) => {
                if (availablePaints.length === 0) {
                    return availablePaints;
                }
                return [];
            });

            return;
        }

        locationFetcher.current.submit(
            { link: projectLink },
            {
                method: 'get',
                action: QUERY_PAINT_COLOURS,
                preventScrollReset: true,
            },
        );
    }, [isEnabled, locationFetcher, projectLink]);

    useEffect(() => {
        if (colourFetcher.state !== 'idle') return;
        const newColours =
            colourFetcher.data?.map((colour) => ({
                id: colour.ID,
                title: colour.Title,
                code: colour.Code,
                hex: colour.Hex,
                productId: colour.ProductID,
                rgb: { r: colour.R, g: colour.G, b: colour.B },
                userSelected: false,
                isWoodstain: colour.Code.startsWith('W'),
            })) ?? [];

        setAvailablePaints(newColours);
    }, [colourFetcher.state, colourFetcher.data]);

    const nearestColor = useMemo(() => {
        return nearestColorLib.from(
            availablePaints.map((paint) => {
                return paint.rgb;
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
            }) as any,
        );
    }, [availablePaints]);

    useEffect(() => {
        if (!fetcher.data) return;
        const paintColourProducts = fetcher.data;

        // desktop selection
        setPaintSelection((state) => {
            if (!state) {
                return [];
            }

            return state.map((paint) => {
                const returnedPaint = paintColourProducts.find(
                    (paintColourProduct) =>
                        paintColourProduct.ID === paint.productId,
                );
                if (returnedPaint) {
                    return {
                        ...paint,
                        link: returnedPaint?.Link,
                        variantId: returnedPaint?.Variants?.[0]?.ID,
                    };
                } else {
                    return paint;
                }
            });
        });

        //mobile selection
        dispatch({
            type: 'SET_PREVIEW_PAINT_MOBILE',
            payload: paintColourProducts,
        });
    }, [fetcher.data, setPaintSelection]);

    const prevCartFetcherState = usePrevious(cartFetcher.state);
    useEffect(() => {
        if (
            showToCartToast &&
            prevCartFetcherState !== 'idle' &&
            cartFetcher.state === 'idle' &&
            cartFetcher.data
        ) {
            toast(`Sample has been added to your cart`, {
                type: 'success' as const,
                clickHereLink: {
                    as: 'a',
                    href: `/shopping-cart`,
                    children: 'View here',
                },
            });

            setShowToCartToast(false);
        }

        dispatch({
            type: 'TOGGLE_ADDING_TO_CART',
            payload: cartFetcher.state !== 'idle',
        });
    }, [
        cartFetcher.data,
        cartFetcher.state,
        prevCartFetcherState,
        showToCartToast,
        toast,
    ]);

    const prevSavePaletteFetcherState = usePrevious(savePaletteFetcher.state);
    useEffect(() => {
        if (
            showSavePaletteToast &&
            prevSavePaletteFetcherState !== 'idle' &&
            savePaletteFetcher.state === 'idle' &&
            savePaletteFetcher.data &&
            'title' in savePaletteFetcher.data
        ) {
            const data = savePaletteFetcher.data as SavePinsToBoardResponse;

            showSuccessToast({
                actionTaken: 'saved',
                boardName: data.title,
                pinCount: paintSelection.length,
                urlSegment: '',
            });

            setShowSavePaletteToast(false);
        }

        dispatch({
            type: 'TOGGLE_SAVING_PALETTE',
            payload: savePaletteFetcher.state !== 'idle',
        });
    }, [
        savePaletteFetcher.state,
        savePaletteFetcher.data,
        prevSavePaletteFetcherState,
        showSuccessToast,
        paintSelection.length,
        showSavePaletteToast,
    ]);

    const fetchPaintColourProduct = useCallback(
        (paints: PaintProduct[]) => {
            const formData = new FormData();
            paints.forEach((paint) => {
                if (!paint.link || !paint.variantId) {
                    formData.append('id', paint.productId.toString());
                }
            });
            if (!formData.getAll('id').length) return;
            fetcher.submit(formData, {
                method: 'get',
                action: QUERY_PAINT_COLOUR_PRODUCT,
                preventScrollReset: true,
            });
        },
        [fetcher],
    );

    const [lastTrackedPaint, setLastTrackedPaint] =
        useState<PaintProduct | null>(null);

    const trackDetectedColor = useCallback(
        (paint: PaintProduct) => {
            if (
                lastTrackedPaint === null ||
                lastTrackedPaint.productId !== paint.productId
            ) {
                tracker.log('ReseneToolPaintDetected', {
                    url: new URL(window.location.href),
                    data: {
                        ExtraData: JSON.stringify({
                            Paint: getPaintDataForTracking(paint),
                        }),
                    },
                });

                setLastTrackedPaint(paint);
            }
        },
        [tracker, lastTrackedPaint, setLastTrackedPaint],
    );

    const { run: debouncedTrackDetectedColor } = useDebounceFn(
        trackDetectedColor,
        {
            wait: TRACKING_PAINT_DETECTION_DEBOUNCE,
            leading: true,
        },
    );

    const matchRgbToReseneColour = useCallback(
        (rgb: RGB): PaintProduct | null => {
            const rgbStringify: string = `${rgb.r}-${rgb.g}-${rgb.b}`;

            if (availablePaints.length === 0) return null;

            if (state.colourCache.has(rgbStringify)) {
                return state.colourCache.get(rgbStringify) || null;
            }

            const nearestReseneHex = nearestColor(rgb)?.toLowerCase();

            const paintFound = availablePaints.find(
                (x) => x.hex.toLowerCase() === nearestReseneHex,
            );

            if (paintFound) {
                dispatch({
                    type: 'SET_COLOUR_CACHE',
                    payload: state.colourCache.set(rgbStringify, paintFound),
                });
            }

            return paintFound || null;
        },
        [availablePaints, state.colourCache, dispatch, nearestColor],
    );

    const getPaintDataForTracking = (paint: PaintProduct) => ({
        Title: paint.title,
        Link: paint.link,
        ProductId: paint.productId,
    });

    const goToProductPage = useCallback(
        (paint: PaintProduct) => {
            tracker.log('ReseneToolProductViewed', {
                url: new URL(window.location.href),
                data: {
                    ExtraData: JSON.stringify({
                        Paint: getPaintDataForTracking(paint),
                    }),
                },
            });
            navigate({ pathname: paint.link });
        },
        [navigate, tracker],
    );

    const openPaintPicker = useCallback(() => {
        if (!state.paintPickerActive) {
            tracker.log('ReseneToolOpened', {
                url: new URL(window.location.href),
            });
            dispatch({ type: 'OPEN_PAINT_PICKER' });
        }
    }, [state.paintPickerActive, tracker]);

    const onSelectPreview = useCallback(
        (paint: PaintProduct | null, fetchProductInfo?: boolean) => {
            dispatch({ type: 'SET_PREVIEW_PAINT', payload: paint });

            if (paint) {
                debouncedTrackDetectedColor(paint);

                if (fetchProductInfo) {
                    const formData = createPaintFormData([paint]);
                    const hasProductsToFetch =
                        Array.from(formData.values()).length > 0;
                    if (hasProductsToFetch) {
                        fetcher.submit(formData, {
                            method: 'get',
                            action: QUERY_PAINT_COLOUR_PRODUCT,
                            preventScrollReset: true,
                        });
                    }
                }
            }
        },
        [fetcher, debouncedTrackDetectedColor],
    );

    const addToCart = useCallback(
        (id: number) => {
            if (!state.isAddingToCart) {
                cartFetcher.submit(
                    {
                        variant: String(id),
                        quantity: String(1),
                        sample: 'false',
                    },
                    {
                        method: 'post',
                        action: '/remix-api/ecommerce/add-to-shopping-cart',
                    },
                );

                setShowToCartToast(true);
            }
        },
        [cartFetcher, state.isAddingToCart],
    );

    const savePaletteToDesignBoard = useCallback(
        (boardId: number, title?: string) => {
            const pins = paintSelection.map((paint) => ({
                id: paint.productId,
                type: 'ProductPin',
            }));

            const pinsInput = JSON.stringify(pins);
            const boardTitle = title ?? designBoardTitle;

            tracker.log('ReseneToolPaletteSaved', {
                url: new URL(window.location.href),
                data: {
                    ExtraData: JSON.stringify({
                        Board: {
                            ID: boardId,
                            Title: boardTitle,
                        },
                        Paints: paintSelection.map((paint) =>
                            getPaintDataForTracking(paint),
                        ),
                        PaintsCount: paintSelection.length,
                    }),
                },
            });

            savePaletteFetcher.submit(
                {
                    boardId,
                    boardTitle,
                    pins: pinsInput,
                    _action: API_ACTIONS.SAVE_MULTIPLE_ITEMS,
                },
                {
                    method: 'post',
                    action: DESIGN_BOARD_REMIX_API,
                },
            );

            setShowSavePaletteToast(true);
        },
        [savePaletteFetcher, paintSelection, tracker],
    );

    const closePaintPicker = useCallback(() => {
        if (state.paintPickerActive) {
            tracker.log('ReseneToolClosed', {
                url: new URL(window.location.href),
            });
            dispatch({ type: 'CLOSE_PAINT_PICKER' });
        }
    }, [state.paintPickerActive, tracker]);

    const setPickingProcess = useCallback((active: boolean) => {
        dispatch({ type: 'TOGGLE_PICKING', payload: active });
    }, []);

    const setSelectedColour = useCallback((index: number | null) => {
        dispatch({ type: 'SET_ACTIVE_COLOUR_INDEX', payload: index });
    }, []);

    const setUsageHint = useCallback((active: boolean) => {
        dispatch({ type: 'TOGGLE_USAGE_HINT', payload: active });
    }, []);

    const addPaintToSelection = useCallback(
        (paint?: PaintProduct) => {
            const { selectedColorIndex, previewPaint } = state;
            const selectedPaint = paint ?? previewPaint;

            if (selectedPaint) {
                // any interaction with the selection means the user claims the selection as their own selection.
                paintSelection.forEach((p) => (p.userSelected = true));
                selectedPaint.userSelected = true;

                const paintToReplace =
                    selectedColorIndex !== null
                        ? paintSelection[selectedColorIndex]
                        : null;
                const newState =
                    selectedColorIndex !== null
                        ? [
                              ...paintSelection
                                  .slice(0, selectedColorIndex)
                                  .filter((i) => i.id !== selectedPaint.id),
                              selectedPaint,
                              ...paintSelection
                                  .slice(selectedColorIndex + 1)
                                  .filter((i) => i.id !== selectedPaint.id),
                          ]
                        : [
                              ...paintSelection.filter(
                                  (i) => i.id !== selectedPaint.id,
                              ),
                              selectedPaint,
                          ];

                setPaintSelection(newState.slice(-PALETTE_COLOUR_LIMIT));

                const baseTrackingData = {
                    Paint: getPaintDataForTracking(selectedPaint),
                    PaintsCount: newState.length,
                };

                tracker.log(
                    selectedColorIndex
                        ? 'ReseneToolPaintReplaced'
                        : 'ReseneToolPaintAdded',
                    {
                        url: new URL(window.location.href),
                        data: {
                            ExtraData: JSON.stringify(
                                paintToReplace
                                    ? {
                                          ...baseTrackingData,
                                          ReplacedPaint:
                                              getPaintDataForTracking(
                                                  paintToReplace,
                                              ),
                                      }
                                    : baseTrackingData,
                            ),
                        },
                    },
                );

                fetchPaintColourProduct(newState);
                dispatch({ type: 'TOGGLE_POPULATE_DEFAULT', payload: false });
                dispatch({ type: 'SET_ACTIVE_COLOUR_INDEX', payload: null });
            }
        },
        [
            fetchPaintColourProduct,
            paintSelection,
            setPaintSelection,
            state,
            tracker,
        ],
    );

    const updatePaintSelection = useCallback(
        (paints: PaintProduct[], populateDefaults = false) => {
            // Only fully update selection if the selection contains no user selected paints
            if (!paintSelection.find((p) => p.userSelected)) {
                setPaintSelection(paints);
                fetchPaintColourProduct(paints);
                dispatch({
                    type: 'TOGGLE_POPULATE_DEFAULT',
                    payload: populateDefaults,
                });
            }
        },
        [fetchPaintColourProduct, paintSelection, setPaintSelection],
    );

    const removePaint = useCallback(
        (id: number) => {
            if (paintSelection.length > 0) {
                const removePaint = paintSelection.find((p) => p.id === id);

                if (removePaint) {
                    const newState = paintSelection.filter((p) => p.id !== id);
                    // any interaction with the selection means the user claims the selection as their own selection.
                    newState.forEach((p) => (p.userSelected = true));
                    setPaintSelection(newState);

                    tracker.log('ReseneToolPaintRemoved', {
                        url: new URL(window.location.href),
                        data: {
                            ExtraData: JSON.stringify({
                                Paint: getPaintDataForTracking(removePaint),
                                PaintsCount: newState.length,
                            }),
                        },
                    });

                    dispatch({
                        type: 'SET_ACTIVE_COLOUR_INDEX',
                        payload: null,
                    });
                    dispatch({
                        type: 'TOGGLE_POPULATE_DEFAULT',
                        payload: false,
                    });
                }
            }
        },
        [paintSelection, setPaintSelection, tracker],
    );

    const addPaintToCart = useCallback(
        (paint: PaintProduct) => {
            if (paint.variantId) {
                addToCart(paint.variantId);

                tracker.log('ReseneToolSampleAddedToCart', {
                    url: new URL(window.location.href),
                    data: {
                        ExtraData: JSON.stringify({
                            Paint: getPaintDataForTracking(paint),
                        }),
                    },
                });
            }
        },
        [addToCart, tracker],
    );

    return (
        <PaintPickerContext.Provider
            value={{
                state,
                dispatch,
                paintSelection,
                matchRgbToReseneColour,
                openPaintPicker,
                closePaintPicker,
                setPickingProcess,
                setUsageHint,
                onSelectPreview,
                addPaintToSelection,
                updatePaintSelection,
                removePaint,
                goToProductPage,
                setSelectedColour,
                addPaintToCart,
                savePaletteToDesignBoard,
                availablePaints,
                containerRef,
            }}
        >
            {children}
            {availablePaints.length !== 0 && (
                <PaintPicker bannerUrl={bannerUrl} />
            )}
        </PaintPickerContext.Provider>
    );
};
